import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Eye, EyeOff, ShieldCheck, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [emailMasked, setEmailMasked] = useState("");
  const [nombre, setNombre] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setStatus("invalid"); return; }
      try {
        const r = await axios.post(`${API}/auth/public/reset-password/verify`, { token });
        setEmailMasked(r.data.email_masked || "");
        setNombre(r.data.nombre || "");
        setStatus("valid");
      } catch {
        setStatus("invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (pwd !== pwd2) { toast.error("Las contraseñas no coinciden"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/public/reset-password/confirm`, { token, new_password: pwd });
      toast.success("Contraseña restablecida. Ya puede iniciar sesión.");
      setStatus("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo restablecer la contraseña");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }} />
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium">
        <ArrowLeft className="w-4 h-4" /> Volver al inicio
      </Link>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 relative z-10">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mb-3">
            <ShieldCheck className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="font-display font-extrabold text-2xl text-green-900">Restablecer contraseña</h1>
          <p className="text-sm text-slate-500 mt-1">Portal del Contribuyente · SISAUR</p>
        </div>

        {status === "checking" && (
          <div className="text-center py-8" data-testid="reset-status-checking">
            <div className="w-10 h-10 border-4 border-green-700 border-t-orange-500 rounded-full animate-spin mx-auto" />
            <p className="mt-3 text-slate-600 text-sm">Verificando enlace…</p>
          </div>
        )}

        {status === "invalid" && (
          <div className="text-center py-6" data-testid="reset-status-invalid">
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <p className="mt-3 text-red-700 font-semibold">Enlace inválido o expirado</p>
            <p className="mt-2 text-sm text-slate-600">Solicite un nuevo enlace comunicándose con la Alcaldía.</p>
            <Button className="mt-6 w-full bg-green-700 hover:bg-green-800 text-white" onClick={() => navigate("/login/contribuyente")}>
              Ir al inicio de sesión
            </Button>
          </div>
        )}

        {status === "done" && (
          <div className="text-center py-6" data-testid="reset-status-done">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
            <p className="mt-3 text-green-800 font-semibold">¡Contraseña actualizada!</p>
            <p className="mt-2 text-sm text-slate-600">Ya puede iniciar sesión con su nueva contraseña.</p>
            <Button data-testid="reset-go-login" className="mt-6 w-full bg-green-700 hover:bg-green-800 text-white" onClick={() => navigate("/login/contribuyente")}>
              Iniciar sesión
            </Button>
          </div>
        )}

        {status === "valid" && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            {nombre && (
              <div className="text-center text-sm text-slate-600 pb-2">
                Cambiaremos la contraseña de <strong className="text-green-900">{nombre}</strong>
                {emailMasked && <div className="text-xs text-slate-500 mt-0.5">{emailMasked}</div>}
              </div>
            )}
            <div>
              <Label>Nueva contraseña*</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="reset-pwd-input" type={showPwd ? "text" : "password"} className="pl-9 pr-10"
                  value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Mínimo 6 caracteres" required />
                <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmar contraseña*</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="reset-pwd2-input" type={showPwd ? "text" : "password"} className="pl-9"
                  value={pwd2} onChange={e => setPwd2(e.target.value)} placeholder="Repita la contraseña" required />
              </div>
            </div>
            <Button data-testid="reset-submit" type="submit" disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 text-white h-11 rounded-full font-semibold">
              {saving ? "Guardando…" : "Restablecer contraseña"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
